// uiarc: toast-stack kuyruğu ve yığın yerleşimi (https://uiarc.dev/r/toast-stack.json) — React'ten bağımsız saf
// mantık: kuyruk (`createToastStore`) ve yığın geometrisi (`layoutStack`). Görünüm `toast-stack.tsx`te.
import type { ReactNode } from 'react'

export type ToastType = 'success' | 'info' | 'warning' | 'error' | 'loading'

export interface ToastAction {
  label: string
  /** Eylemi çalıştırır. İşleyici bildirimi güncellemedikçe sonrasında kapanır; "Geri al" bildirimi sonucuna dönüştürebilir. */
  onClick: (id: string) => void
}

export interface ToastOptions {
  /** Aynı kimlik, yeni bildirim yığmak yerine var olanı yerinde günceller. */
  id?: string
  type?: ToastType
  title: string
  description?: string
  action?: ToastAction
  /** Kendiliğinden kapanma süresi (ms). Türe göre varsayılan; `loading` güncelleme bekler. Kapatılana dek kalsın diye `Infinity`. */
  duration?: number
}

export interface ToastStackApi {
  /** Bildirimi gösterir, kimliğini döndürür. */
  toast: (options: ToastOptions) => string
  /** Bildirimi yerinde dönüştürür: simge, metin ve yükseklik yeni içeriğe animasyonla geçer, süre yeniden başlar. Eylemi kaldırmak için `action: undefined`. */
  update: (id: string, patch: Partial<Omit<ToastOptions, 'id'>>) => void
  /** Bir bildirimi, kimlik verilmezse hepsini kapatır. */
  dismiss: (id?: string) => void
}

export interface ToastStackProviderProps {
  children: ReactNode
  /** Temel ömür (ms). Uyarı ve hata 1,6 kat uzun kalır. */
  duration?: number
  /** Sınırı aşan en eski bildirimler kuyruktan düşer. */
  limit?: number
}

export type ToastRecord = {
  id: string
  type: ToastType
  title: string
  description?: string
  action?: ToastAction
  duration: number
  seq: number
  version: number
}
export type ToastStore = ToastStackApi & {
  subscribe: (listener: () => void) => () => void
  getSnapshot: () => ToastRecord[]
  runAction: (id: string) => void
}
export type Target = { y: number; scale: number; height: number; opacity: number; content: number }

/** Arkaya yığılan her bildirim öndekinin üstünde bu kadar piksel görünür. */
const PEEK = 14
/** Yığın açıkken bildirimler arası boşluk. */
const GAP = 12
/** Arkaya yığılan bildirim her adımda bu kadar küçülür. */
const STEP = 0.05
/** Kartın üst ve alt kenarlığı; ölçülen içerik yüksekliğine eklenir. */
export const BORDER = 2
/** Bu kadar sağa kaydırma çoğunlukla solmuştur. */
export const SWIPE_FADE = 280
const durationFor = (type: ToastType, base: number) =>
  type === 'loading' ? Infinity : type === 'warning' || type === 'error' ? base * 1.6 : base
/**
 * Kapalıyken arkadaki bildirimler öndekinin yüksekliğini alır, kenarlar hizalanır; açıkken her biri kendi
 * yüksekliğini alır ve aralıklı dizilir. Liste üzerine gelme hedefidir ve boşlukları kaplar, açık bildirimler
 * arasında gezinmek yığını kapatmaz.
 */
export function layoutStack(
  toasts: ToastRecord[],
  heights: Record<string, number>,
  expanded: boolean,
  visibleToasts: number,
) {
  const front = toasts.length ? (heights[toasts[0]!.id] ?? 0) : 0
  const tops = toasts.reduce<number[]>(
    (sum, item, index) => [
      ...sum,
      sum[index]! + (index < visibleToasts ? (heights[item.id] ?? 0) + GAP : 0),
    ],
    [0],
  )
  const targets = toasts.map((item, index): Target => {
    const visible = index < visibleToasts
    const depth = Math.min(index, visibleToasts)
    return expanded
      ? {
          y: -tops[index]!,
          scale: 1,
          height: heights[item.id] ?? 0,
          opacity: visible ? 1 : 0,
          content: 1,
        }
      : {
          y: -PEEK * depth,
          scale: 1 - STEP * depth,
          height: index === 0 ? (heights[item.id] ?? 0) : front,
          opacity: visible ? 1 : 0,
          content: index === 0 ? 1 : 0,
        }
  })
  const listHeight = !toasts.length
    ? 0
    : expanded
      ? tops[toasts.length]! - GAP
      : front + PEEK * (Math.min(toasts.length, visibleToasts) - 1)
  return { targets, listHeight }
}

export function createToastStore(base: number, limit: number): ToastStore {
  let toasts: ToastRecord[] = []
  let seq = 0
  const listeners = new Set<() => void>()
  const commit = (next: ToastRecord[]) => {
    toasts = next
    listeners.forEach((listener) => listener())
  }
  const update: ToastStackApi['update'] = (id, patch) => {
    const found = toasts.find((item) => item.id === id)
    if (!found) return
    const type = patch.type ?? found.type
    const record: ToastRecord = {
      ...found,
      ...patch,
      type,
      duration: patch.duration ?? durationFor(type, base),
      version: found.version + 1,
    }
    commit(toasts.map((item) => (item.id === id ? record : item)))
  }
  const toast: ToastStackApi['toast'] = ({ id, ...options }) => {
    if (id && toasts.some((item) => item.id === id)) {
      update(id, options)
      return id
    }
    const type = options.type ?? 'info'
    seq += 1
    const record: ToastRecord = {
      ...options,
      id: id ?? `toast-${seq}`,
      type,
      duration: options.duration ?? durationFor(type, base),
      seq,
      version: 0,
    }
    commit([record, ...toasts].slice(0, limit))
    return record.id
  }
  const dismiss: ToastStackApi['dismiss'] = (id) => {
    if (id === undefined) {
      if (toasts.length) commit([])
      return
    }
    if (toasts.some((item) => item.id === id)) commit(toasts.filter((item) => item.id !== id))
  }
  const runAction = (id: string) => {
    const found = toasts.find((item) => item.id === id)
    if (!found?.action) return
    found.action.onClick(id)
    if (toasts.find((item) => item.id === id)?.version === found.version) dismiss(id)
  }
  return {
    toast,
    update,
    dismiss,
    runAction,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    getSnapshot: () => toasts,
  }
}
